import { FC, MouseEvent } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@apollo/client'
import {
  CreateVisitInterestMutationVariables,
  CreateVisitInterestMutation,
  CreateBuyerLeadVisitInterestMutation,
  CreateBuyerLeadVisitInterestMutationVariables,
  Listing,
  User,
  MainInterests,
  BuyerPaymentType,
} from 'graphql/types'
import isNil from 'lodash/isNil'
import logger from 'lib/logger'
import { toCurrencyNoStyle, toValidNumber } from 'lib/number'
import { clearApolloStore, onUserLoginWebviewMessage, setJwt } from 'lib/user'
import { getDateTimeProperties, getDateToISO } from 'lib/date'
import { IconArrowLeft, IconClock } from 'icons'
import { getUTMObject } from 'lib/utm'
import { getEmCasaSessionListings } from 'lib/listing'
import CREATE_VISIT_INTEREST from 'graphql/mutations/createVisitInterest'
import CREATE_BUYER_LEAD_VISIT_INTEREST from 'graphql/mutations/createBuyerLeadVisitInterest'
import useStep from 'hooks/useStep'
import Loader from 'components/Loader'
import Modal from 'components/Modal'
import Button from 'components/Button'
import Typography from 'components/Typography'
import FeatureItem from 'components/FeatureItem'
import {
  closeVisitEvent,
  backVisitEvent,
  sendVisitEvent,
  stepVisitEvent,
  visitErrorEvent,
  visitSuccessEvent,
  sendBuyerVisitEvent,
} from 'pages/imoveis/imovel/events'
import { TBuyerFormValues, TGetStepProps } from './types'
import { getSteps } from './steps'
import styles from './VisitModal.module.scss'

export type TProps = {
  withBuyerLead?: boolean
  visible: boolean
  listing: Pick<Listing, 'id' | 'uuid' | 'address'>
  profile?: Pick<
    User,
    | 'id'
    | 'name'
    | 'phone'
    | 'email'
    | 'buyerIntent'
    | 'paymentType'
    | 'hasApprovedCredit'
    | 'cpf'
    | 'incomeMonthly'
    | 'creditTotalValue'
    | 'listingAnnounced'
    | 'hasSearchPreferences'
  > | null
  onClose: () => void
} & Pick<TGetStepProps, 'onSuccessButtonClick'>

const VisitModal: FC<TProps> = ({
  withBuyerLead,
  visible,
  onClose,
  listing,
  profile,
  onSuccessButtonClick,
}) => {
  const defaultValues: TBuyerFormValues = {
    id: profile?.id || '',
    name: profile?.name || '',
    phone: profile?.phone || '',
    email: profile?.email || '',
    appointment: {},
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    mainInterest: withBuyerLead
      ? (profile?.buyerIntent?.mainInterest as MainInterests)
      : undefined,
    paymentType: withBuyerLead
      ? (profile?.paymentType as BuyerPaymentType)
      : undefined,
    hasApprovedCredit: !isNil(profile?.hasApprovedCredit)
      ? String(profile?.hasApprovedCredit)
      : undefined,
    cpf: profile?.cpf || '',
    incomeMonthly:
      profile?.incomeMonthly && toValidNumber(profile?.incomeMonthly)
        ? toCurrencyNoStyle(profile?.incomeMonthly as unknown as number)
        : '',
    creditTotalValue:
      profile?.creditTotalValue && toValidNumber(profile?.creditTotalValue)
        ? toCurrencyNoStyle(profile?.creditTotalValue as unknown as number)
        : '',
    listingAnnounced: !isNil(profile?.listingAnnounced)
      ? String(profile?.listingAnnounced)
      : undefined,
  }

  const {
    control,
    register,
    watch,
    formState: { isValid },
    reset: resetForm,
    setValue,
  } = useForm<TBuyerFormValues>({
    mode: 'onChange',
    defaultValues,
  })

  const [createVisitInterest, { error, loading }] = useMutation<
    CreateVisitInterestMutation,
    CreateVisitInterestMutationVariables
  >(CREATE_VISIT_INTEREST)

  const [
    createBuyerLeadVisitInterest,
    { error: buyerLeadError, loading: buyerLeadLoading },
  ] = useMutation<
    CreateBuyerLeadVisitInterestMutation,
    CreateBuyerLeadVisitInterestMutationVariables
  >(CREATE_BUYER_LEAD_VISIT_INTEREST)

  const {
    canGoToPrevStep,
    canGoToNextStep,
    goToNextStep,
    goToPrevStep,
    currentStep,
    setStep,
    reset: resetSteps,
  } = useStep(5)

  const getEventProps = () => {
    const { email, name, phone, mainInterest, paymentType } = watch()
    const viewedListings = getEmCasaSessionListings()
    return {
      listing,
      weekday:
        watchDate?.date && getDateTimeProperties(watchDate?.date).weekFullDay,
      date: watchDate?.dateString,
      hour: watchDate?.time,
      name,
      phone,
      email,
      mainInterest,
      paymentType,
      skipBuyerLeadSchedule: false,
      viewedListings,
      totalViewedListings: viewedListings.length,
    }
  }
  const handleClose = () => {
    onClose()
    resetForm()
    setTimeout(() => resetSteps(), 1000)
    closeVisitEvent()
  }

  const watchDate = watch('appointment')
  const properties = watchDate?.date
    ? getDateTimeProperties(watchDate?.date)
    : null

  const handleDisabledNext = () => {
    if (currentStep === 1) {
      return !watchDate?.time
    }
    if (currentStep === 3) {
      return true
    }
    return !isValid
  }

  const handleNext = (event: MouseEvent<HTMLElement>) => {
    if (event) event.preventDefault()

    if (currentStep === 1) {
      goToNextStep()
      stepVisitEvent({ step: 'profile' })
    }

    if (currentStep === 2 && watchDate?.date) {
      if (withBuyerLead) {
        setStep(profile?.id ? 4 : 3)
        stepVisitEvent({ step: profile?.id ? 'buyer' : 'login' })
      } else {
        const { email, name, phone } = watch()
        sendVisitEvent()
        createVisitInterest({
          variables: {
            input: {
              utm: getUTMObject() as Record<string, string | null>,
              appointment: getDateToISO(watchDate.date),
              listingId: listing.id as string,
              name,
              phone,
              email,
            },
          },
        })
          .then(({ data }) => {
            try {
              setJwt(data?.interestCreate?.credentials?.jwt as string)
              onUserLoginWebviewMessage(
                data?.interestCreate?.credentials?.jwt as string,
              )
              void clearApolloStore()
              logger.identify(data?.interestCreate?.credentials?.user)
              visitSuccessEvent({ ...getEventProps() })
              setStep(5)
            } catch (e) {
              logger.action('site-cookie-error', { error: e })
              logger.capture(e, 'Schedule Visit Error')
            }
          })
          .catch((e) => {
            setStep(5)
            visitErrorEvent({
              ...getEventProps(),
              error: JSON.stringify(e),
            })
            logger.capture(e, 'Schedule Visit Error')
          })
      }
    }

    if (currentStep === 4 && watchDate?.date) {
      const {
        email,
        name,
        phone,
        paymentType,
        cpf,
        hasApprovedCredit,
        listingAnnounced,
        incomeMonthly,
        creditTotalValue,
        mainInterest,
      } = watch()

      const variables: CreateBuyerLeadVisitInterestMutationVariables = {
        interestInput: {
          utm: getUTMObject() as Record<string, string | null>,
          appointment: getDateToISO(watchDate.date),
          listingId: listing.id as string,
          name,
          phone,
          email,
        },
        id: profile?.id as string,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        mainInterest: !isNil(mainInterest) ? mainInterest : undefined,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        paymentType:
          paymentType as CreateBuyerLeadVisitInterestMutationVariables['paymentType'],
        hasApprovedCredit: undefined,
        creditTotalValue: undefined,
        cpf: undefined,
        incomeMonthly: undefined,
      }

      if (paymentType === 'DEFERRED' || paymentType === 'CONSORTIUM') {
        variables.hasApprovedCredit = hasApprovedCredit === 'true'

        if (variables.hasApprovedCredit) {
          variables.creditTotalValue =
            creditTotalValue && String(toValidNumber(creditTotalValue))
        } else {
          variables.cpf = cpf

          if (paymentType === 'DEFERRED') {
            variables.incomeMonthly =
              incomeMonthly && String(toValidNumber(incomeMonthly))
          }
        }
      }

      if (paymentType === 'NEED_TO_SELL') {
        variables.listingAnnounced = listingAnnounced === 'true'
      }
      sendBuyerVisitEvent()
      createBuyerLeadVisitInterest({ variables })
        .then(() => {
          void clearApolloStore()
          visitSuccessEvent({ ...getEventProps() })
          setStep(5)
        })
        .catch((e) => {
          setStep(4)
          logger.capture(e, 'Buyer Lead Schedule Visit Error')
        })
    }
  }

  const currentStepObject = getSteps({
    listing,
    control,
    register,
    setValue,
    watch,
    status: error || buyerLeadError ? 'error' : 'success',
    setStep,
    profile,
    onSuccessButtonClick,
  })[currentStep]

  return (
    <Modal
      bottom
      header={{
        leadingContent: canGoToPrevStep && currentStep !== 5 && (
          <Button
            aria-label="Voltar"
            theme="ghost"
            shape="circle"
            onClick={() => {
              backVisitEvent()
              if (currentStep === 4) {
                setStep(2)
              } else {
                goToPrevStep()
              }
            }}
            Icon={{
              Component: IconArrowLeft,
              color: 'grey900',
              title: 'Voltar',
            }}
          />
        ),
        title: 'Visitar',
      }}
      testId="ecVisitModal"
      visible={visible}
      handleCloseButtonClick={handleClose}
      handleBackgroundClick={handleClose}
      footer={
        !canGoToNextStep || currentStep === 3
          ? undefined
          : {
              align: 'between',
              children: (
                <div>
                  {watchDate?.time && (
                    <FeatureItem
                      Icon={{
                        Component: IconClock,
                        color: 'grey900',
                        title: 'Data da visita',
                      }}
                      title={
                        properties
                          ? `${properties?.day}/${properties?.month}, ${properties?.weekDay} às ${properties?.hour}:${properties?.min}`
                          : ''
                      }
                      className={styles.ecModalFooterAlignment}
                    />
                  )}
                </div>
              ),
              buttons: [
                {
                  label:
                    currentStep === 3 || (currentStep === 2 && !withBuyerLead)
                      ? 'Solicitar'
                      : 'Avançar',
                  props: {
                    theme: 'pink',
                    onClick: handleNext,
                    disabled: handleDisabledNext(),
                  },
                },
              ],
            }
      }
    >
      <div className={styles.ecVisitModal}>
        {buyerLeadError && (
          <div className={styles.ecQEVBannerErrorWrapper}>
            <div className={styles.ecQEVBannerError}>
              <Typography as="p" variant="Paragraph">
                Erro ao enviar. Revise os seus dados e tente novamente
              </Typography>
            </div>
          </div>
        )}
        {loading || buyerLeadLoading ? <Loader /> : currentStepObject.content}
      </div>
    </Modal>
  )
}

export default VisitModal
